/**
 * Tipos y llamadas REALES de `consola/incidentes` (`apps/api/src/incidentes/consola-incidentes.
 * controller.ts`). Mismo criterio que `dominio/verificacionApi.ts`: estos tipos NO son los de
 * `dominio/contratos.ts` — son DISTINTOS a propósito.
 *
 * 🔴 **Los mock de `dominio/contratos.ts` (`TipoIncidente`, `EstadoIncidente`, `Incidente`) NO
 * COINCIDEN con lo que el backend real devuelve.** Medido leyendo `apps/api/prisma/schema.prisma`
 * + VERIFICADO POR EFECTO contra un backend real (Postgres real, migraciones aplicadas, los tres
 * endpoints ejercitados con un token `Bearer` — ver el reporte de la sesión):
 *
 * ```
 *                    MOCK (dominio/contratos.ts)                    REAL (apps/api)
 * TipoIncidente      SOS·DISPUTA·RECLAMO·ACCIDENTE·OTRO             SOS·ACCIDENTE·DISPUTA_RUTA·
 *                                                                    NO_SHOW·CONDUCTA·OTRO
 * EstadoIncidente    ABIERTO·EN_ATENCION·RESUELTO·CERRADO           ABIERTO·EN_ATENCION·CERRADO
 *                                                                    (sin RESUELTO — es binario:
 *                                                                    o sigue abierto/en atención,
 *                                                                    o está cerrado)
 * ```
 *
 * No es un descuido de esta pantalla: el mock lo declaró otra ronda ANTES de que existiera
 * `apps/api`, igual que pasó con Verificación (ver la cabecera de `verificacionApi.ts`). Reusar
 * esos tipos acá habría compilado igual y mostrado datos reales con etiquetas EQUIVOCADAS para
 * `DISPUTA_RUTA`/`NO_SHOW`/`CONDUCTA` (no existen en el diccionario mock) — por eso este archivo
 * declara sus propios tipos y `Incidentes.tsx` su propio diccionario de etiquetas, sin tocar
 * `componentes/Etiqueta.tsx` (que sigue indexado por los tipos MOCK y lo usan otras pantallas).
 *
 * ⚠️ **La fila real NO trae nombres — sólo ids.** `cola()`/`atender()`/`cerrar()` en
 * `incidentes.service.ts` hacen `findMany`/`update` SIN `include` de las relaciones
 * (`reportante`/`operador`/`viaje`) ⇒ la respuesta trae `reportanteId`/`operadorId`/`viajeId`
 * crudos, nunca `reportanteNombre`/`operadorNombre` (que sí tenía el mock). Mismo patrón que
 * `dispatchApi.ts` ya documentó para `conductorId` sin nombre: no hay hoy un listado general de
 * usuarios con nombre que cruzar acá. `Incidentes.tsx` muestra el id crudo.
 */
import { obtener, postear } from './clienteApi';

// ---------------------------------------------------------------------------
// Enums reales — leídos de apps/api/prisma/schema.prisma, no adivinados, y
// VERIFICADOS por efecto (los seis valores de TipoIncidente y los tres de
// EstadoIncidente se produjeron/consumieron de verdad contra un backend real).
// ---------------------------------------------------------------------------

export type TipoIncidenteReal =
  'SOS' | 'ACCIDENTE' | 'DISPUTA_RUTA' | 'NO_SHOW' | 'CONDUCTA' | 'OTRO';

export type EstadoIncidenteReal = 'ABIERTO' | 'EN_ATENCION' | 'CERRADO';

export type ActorTipoReal = 'PASAJERO' | 'CONDUCTOR' | 'SISTEMA' | 'OPERADOR';

export const ESTADOS_INCIDENTE: EstadoIncidenteReal[] = ['ABIERTO', 'EN_ATENCION', 'CERRADO'];

/**
 * `Incidente` de Prisma, tal cual serializa por HTTP. `lat`/`lon` son `Prisma.Decimal?` — cuando
 * tienen valor serializan a STRING (mismo comportamiento que `reputacion` en `verificacionApi.
 * ts`), verificado con `null` por efecto; esta pantalla no los usa (no hay mapa acá).
 */
export interface IncidenteReal {
  id: string;
  viajeId: string | null;
  tipo: TipoIncidenteReal;
  estado: EstadoIncidenteReal;
  reportanteId: string;
  reportanteTipo: ActorTipoReal;
  abiertoAt: string;
  operadorId: string | null;
  atendidoAt: string | null;
  cerradoAt: string | null;
  descripcion: string | null;
  resolucion: string | null;
  lat: string | null;
  lon: string | null;
}

// ---------------------------------------------------------------------------
// GET /consola/incidentes
// ---------------------------------------------------------------------------

/**
 * Sin `estado`, el backend trae TODOS los estados (verificado por efecto). Se valida a mano del
 * lado del backend (no acá) para poder distinguir "sin filtro" de "filtro inválido" — ver el
 * comentario de `consola-incidentes.controller.ts`. Acá no hace falta replicar esa validación:
 * `ESTADOS_INCIDENTE` es la única fuente de valores que esta pantalla ofrece en su selector.
 */
export const obtenerColaIncidentes = (estado?: EstadoIncidenteReal): Promise<IncidenteReal[]> =>
  obtener<IncidenteReal[]>(`/consola/incidentes${estado ? `?estado=${estado}` : ''}`);

// ---------------------------------------------------------------------------
// POST /consola/incidentes/:id/atender
// ---------------------------------------------------------------------------

/**
 * `operadorId` sale del token en el backend, nunca de acá. Rechaza con 400/`incidente_no_abierto`
 * si el incidente ya no está `ABIERTO` (verificado por efecto) — `Incidentes.tsx` deshabilita el
 * botón fuera de ese estado para no depender sólo del mensaje del backend, pero el backend igual
 * lo hace cumplir si algo lo dispara de todos modos (dos pestañas abiertas, por ejemplo).
 */
export const atenderIncidente = (id: string): Promise<IncidenteReal> =>
  postear(`/consola/incidentes/${id}/atender`);

// ---------------------------------------------------------------------------
// POST /consola/incidentes/:id/cerrar
// ---------------------------------------------------------------------------

/**
 * `resolucion` obligatoria, mínimo 10 caracteres (`CerrarIncidenteDto`) — lo hace cumplir
 * `DialogoMotivo`, mismo umbral por coincidencia (los dos usan 10 por su cuenta, no un import
 * compartido). Rechaza con 400/`incidente_ya_cerrado` si ya estaba `CERRADO`. **No exige haber
 * pasado por `atender()` antes**: se puede cerrar directo desde `ABIERTO` (comentario del
 * servicio) — el botón "Cerrar" de la pantalla está habilitado en los dos estados no-terminales.
 */
export const cerrarIncidente = (id: string, resolucion: string): Promise<IncidenteReal> =>
  postear(`/consola/incidentes/${id}/cerrar`, { resolucion });
